import React from 'react'
import { tv } from 'tailwind-variants'
import { cn } from '@scalis/utils'

export type ButtonGroupProps = React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement> & {
  variant?: 'orange' | 'default' | 'blue'
  size?: 'small' | 'medium'
}

const buttonClassName = tv({
  base: cn('flex items-center gap-2', 'text-nowrap body-small-emphasis', 'disabled:cursor-not-allowed'),
  variants: {
    variant: {
      orange: ['border border-dataviz-orange', 'text-dataviz-orange', 'bg-button-orange-rest'],
      default: ['border border-neutral-10', 'text-neutral-primary', 'bg-button-subtle-rest'],
      blue: ['border border-brand-primary-rest', 'text-brand-primary-00', 'bg-button-brand-primary-light-rest'],
    },
    size: {
      medium: 'h-10 py-2 px-4',
      small: 'h-8 py-1 px-3',
    },
    iconOnly: {
      true: '',
    },
    disabled: {
      true: '',
    },
    active: {
      true: '',
    },
  },
  compoundVariants: [
    {
      size: 'medium',
      iconOnly: true,
      className: 'px-3',
    },
    {
      size: 'small',
      iconOnly: true,
      className: 'px-2',
    },
    // disabled state
    {
      variant: 'default',
      disabled: true,
      className: 'text-neutral-disabled border-neutral-disabled',
    },
    {
      variant: 'default',
      disabled: false,
      className: 'hover:bg-button-subtle-hover active:bg-button-subtle-pressed',
    },
    {
      variant: 'orange',
      disabled: true,
      className: 'opacity-50',
    },
    {
      variant: 'orange',
      disabled: false,
      className: 'hover:bg-button-orange-hover active:bg-button-orange-pressed',
    },
    {
      variant: 'blue',
      disabled: true,
      className: 'opacity-50',
    },
    {
      variant: 'blue',
      disabled: false,
      className: 'hover:bg-button-brand-primary-light-hover active:bg-button-brand-primary-light-pressed',
    },
    // active prop
    {
      variant: 'default',
      active: true,
      className: 'bg-button-subtle-pressed',
    },
    {
      variant: 'orange',
      active: true,
      className: 'bg-button-orange-pressed',
    },
    {
      variant: 'blue',
      active: true,
      className: 'bg-button-brand-primary-light-pressed',
    },
  ],
})

export const ButtonGroup = ({
  children,
  variant = 'default',
  size = 'medium',
  className,
  ...rest
}: ButtonGroupProps) => {
  const buttons = React.Children.toArray(children) as React.ReactElement<ButtonProps>[]

  const buttonsWithStyles = buttons.map((button, index) => {
    const isLastButton = index === buttons.length - 1
    const isFirstButton = index === 0
    const isDisabled = button.props.disabled
    const isNextButtonEnabled = !buttons[index + 1]?.props.disabled
    const isPreviousButtonDisabled = buttons[index - 1]?.props.disabled

    let rightBorderVisible = true
    if (isLastButton) {
      rightBorderVisible = true
    } else if (isDisabled && isNextButtonEnabled) {
      rightBorderVisible = false
    }

    let leftBorderVisible = false
    if (isFirstButton) {
      leftBorderVisible = true
    } else if (!isDisabled && isPreviousButtonDisabled) {
      leftBorderVisible = true
    }

    return React.cloneElement(button, {
      className: cn(
        buttonClassName({
          variant,
          size,
          iconOnly: !!button.props.iconOnly,
          disabled: !!button.props.disabled,
          active: !!button.props.active,
        }),
        {
          'rounded-l-xl': isFirstButton,
          'rounded-r-xl': isLastButton,
          'border-r-transparent': !rightBorderVisible,
          'border-l-transparent': !leftBorderVisible,
        },
        button.props.className,
      ),
    })
  })

  return (
    <div
      className={cn('flex flex-row', 'overflow-hidden', className)}
      {...rest}
    >
      {buttonsWithStyles}
    </div>
  )
}

type ButtonProps = React.DetailedHTMLProps<React.ButtonHTMLAttributes<HTMLButtonElement>, HTMLButtonElement> & {
  iconOnly?: boolean
  active?: boolean
  render?: (button: React.ReactNode) => React.ReactNode
}

// iconOnly is handled by the button-group component
const Button = ({ iconOnly, active, render, ...rest }: ButtonProps) => {
  const button = <button {...rest} />
  return render ? render(button) : button
}

const Icon = (props: React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>) => {
  return (
    <i
      aria-hidden
      {...props}
    />
  )
}

ButtonGroup.Button = Button
ButtonGroup.Icon = Icon
